import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import PremiumHeader from '../components/PremiumHeader'

function asNumber(value: number | string | undefined) {
  if (typeof value === 'number') return value
  if (typeof value === 'string') {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : 0
  }
  return 0
}

function money(value: number) {
  return `$${value.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
}

export default function AccountingPage() {
  const { data: invoices = [], isLoading } = useQuery({
    queryKey: ['accounting-invoices'],
    queryFn: async () => (await api.invoices.list()).data,
    refetchInterval: 15000,
  })

  const { data: jobs = [] } = useQuery({
    queryKey: ['accounting-jobs'],
    queryFn: async () => (await api.jobs.list()).data,
    refetchInterval: 15000,
  })

  const { data: loads = [] } = useQuery({
    queryKey: ['accounting-loads'],
    queryFn: async () => (await api.loads.list()).data,
    refetchInterval: 15000,
  })

  const pending = invoices.filter(i => i.status === 'PENDING')
  const paid = invoices.filter(i => i.status === 'PAID')
  const failed = invoices.filter(i => i.status === 'FAILED')

  const pendingAmount = pending.reduce((sum, inv) => sum + asNumber(inv.amount), 0)
  const paidAmount = paid.reduce((sum, inv) => sum + asNumber(inv.amount), 0)
  const failedAmount = failed.reduce((sum, inv) => sum + asNumber(inv.amount), 0)
  const jobsById = new Map(jobs.map(job => [job.id, job]))

  const revenueTotal = loads.reduce((sum, load: any) => sum + asNumber(load.customerPayCents) / 100, 0)
  const carrierPayableTotal = loads.reduce((sum, load: any) => sum + asNumber(load.carrierPayCents) / 100, 0)
  const marginTotal = revenueTotal - carrierPayableTotal
  const marginPercent = revenueTotal > 0 ? (marginTotal / revenueTotal) * 100 : 0

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PremiumHeader
        title="Accounting"
        subtitle="Internal finance workspace for invoices, settlements, and margin controls."
        secondaryAction={
          <Link to="/invoices" className="bg-white border border-gray-200 hover:border-gray-300 text-gray-700 text-sm font-semibold px-4 py-2 rounded-lg">
            Open Invoice Ledger
          </Link>
        }
      />

      {isLoading ? (
        <div className="text-sm text-gray-400">Loading accounting data…</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-amber-700">Pending AR</div>
              <div className="text-3xl font-semibold text-amber-900 mt-1">{money(pendingAmount)}</div>
              <div className="text-xs text-amber-700 mt-1">{pending.length} invoices</div>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-green-700">Collected</div>
              <div className="text-3xl font-semibold text-green-900 mt-1">{money(paidAmount)}</div>
              <div className="text-xs text-green-700 mt-1">{paid.length} invoices</div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-red-700">Exceptions</div>
              <div className="text-3xl font-semibold text-red-900 mt-1">{money(failedAmount)}</div>
              <div className="text-xs text-red-700 mt-1">{failed.length} invoices</div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-blue-700">Revenue</div>
              <div className="text-2xl font-semibold text-blue-900 mt-1">{money(revenueTotal)}</div>
              <div className="text-xs text-blue-700 mt-1">Customer Pay</div>
            </div>
            <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4">
              <div className="text-xs uppercase tracking-wide text-indigo-700">Carrier Payable</div>
              <div className="text-2xl font-semibold text-indigo-900 mt-1">{money(carrierPayableTotal)}</div>
              <div className="text-xs text-indigo-700 mt-1">Carrier Pay</div>
            </div>
            <div className={`border rounded-xl p-4 ${marginTotal >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
              <div className={`text-xs uppercase tracking-wide ${marginTotal >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>Gross Margin</div>
              <div className={`text-2xl font-semibold mt-1 ${marginTotal >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>{money(marginTotal)}</div>
              <div className={`text-xs mt-1 ${marginTotal >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>Revenue - Carrier Payable</div>
            </div>
            <div className={`border rounded-xl p-4 ${marginPercent >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-rose-50 border-rose-200'}`}>
              <div className={`text-xs uppercase tracking-wide ${marginPercent >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>Margin %</div>
              <div className={`text-2xl font-semibold mt-1 ${marginPercent >= 0 ? 'text-emerald-900' : 'text-rose-900'}`}>{marginPercent.toFixed(2)}%</div>
              <div className={`text-xs mt-1 ${marginPercent >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>Gross Margin / Revenue</div>
            </div>
          </div>

          <div className="bg-white border rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Recent Invoice Activity</h2>
              <Link to="/invoices" className="text-sm text-blue-700 hover:underline">View all</Link>
            </div>

            {invoices.length === 0 ? (
              <p className="text-sm text-gray-400">No invoices available yet.</p>
            ) : (
              <div className="space-y-2">
                {invoices.slice(0, 8).map(inv => (
                  <div key={inv.id} className="border rounded-lg p-3 hover:border-blue-300 transition">
                    <div className="flex items-center justify-between gap-3">
                      <Link to={`/invoices/${inv.id}`} className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900">{inv.invoiceNumber}</div>
                        <div className="text-xs text-gray-500 mt-1">{inv.status} {inv.dueDate ? `· Due ${new Date(inv.dueDate).toLocaleDateString()}` : ''}</div>
                        {jobsById.get(inv.jobId)?.proofOfDeliveryUrl ? (
                          <div className="text-xs text-green-700 mt-1">POD: Uploaded</div>
                        ) : (
                          <div className="text-xs text-amber-700 mt-1">POD: Missing</div>
                        )}
                      </Link>
                      <div className="text-sm font-semibold text-gray-800">{money(asNumber(inv.amount))}</div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      {jobsById.get(inv.jobId)?.loadId && (
                        <Link to={`/loads/${jobsById.get(inv.jobId).loadId}/rate-confirmation`} className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50">Rate Confirmation</Link>
                      )}
                      <Link to={`/jobs/${inv.jobId}/dispatch-sheet`} className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50">Dispatch Sheet</Link>
                      <Link to={`/jobs/${inv.jobId}/bol`} className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50">BOL</Link>
                      <Link to={`/jobs/${inv.jobId}/pod`} className="rounded border border-gray-300 px-2 py-1 text-gray-700 hover:bg-gray-50">POD</Link>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
